import React, { useEffect, useState } from 'react';

import { packagingApi as api } from 'api/packaging';
import { procurementApi } from 'api/procurement';
import { DataTable, Notifications } from 'shared';

function ViewPackaging() {
    const [rows, setRows] = useState([]);
    const [packagingTypesOpts, setPackagingTypesOpts] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [vendors, setVendors] = useState([]);
    const [boxOpts, setBoxOpts] = useState([]);
    const [shippingMethodOpts, setShippingMethodOpts] = useState([]);

    const getPackagingTypes = async () => {
        const response = await api.getPackagingTypes();
        if (response.status === false) {
            setNotification({ text: `No packaging types found ${response.msg}`, level: 'error' });
            setPackagingTypesOpts([]);
            return;
        }

        const packagingTypesOpts = response.data.rows.map((row) => ({
            text: row.display_name,
            value: row.system_name,
        }));

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setPackagingTypesOpts([pleaseSelect].concat(packagingTypesOpts));
    };

    // Load
    const getPackaging = async () => {
        const response = await api.getPackaging();
        if (response.status === false) {
            setNotification({ text: `No packaging found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }
        setRows(
            response.data.rows.map((item) => {
                const keys = Object.keys(item.model);
                for (const key of keys) {
                    item[key] = item.model[key];
                }
                return item;
            }),
        );
    };

    const getVendors = async () => {
        const response = await procurementApi.getVendors();
        if (response.status === false) {
            setNotification({ text: `No vendors found ${response.msg}`, level: 'error' });
            setVendors([]);
            return;
        }

        const vendorAsOpts = response.data.rows.map((row) => {
            row.text = row.name;
            row.value = row.id;
            return row;
        });

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setVendors([pleaseSelect].concat(vendorAsOpts));
    };

    const getBoxes = async () => {
        const response = await api.getBoxes();
        if (response.status === false) {
            setNotification({ text: `No Boxes found ${response.msg}`, level: 'error' });
            setBoxOpts([]);
            return;
        }

        const boxOpts = response.data.rows.map((row) => {
            row.text = row.name;
            row.value = row.id;
            return row;
        });

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setBoxOpts([pleaseSelect].concat(boxOpts));
    };

    const getShippingMethods = async () => {
        const response = await api.getShippingMethods();
        if (response.status === false) {
            setNotification({ text: `No shipping methods found ${response.msg}`, level: 'error' });
            setShippingMethodOpts([]);
            return;
        }

        const shippingMethodOpts = response.data.shippingMethods.map((row) => {
            const opt = {};
            opt.text = row;
            opt.value = row;
            return opt;
        });

        setShippingMethodOpts(shippingMethodOpts);
    };

    const filterString = (string) => {
        if (string.includes('model')) {
            return string.substr(6);
        }
        return string;
    };

    useEffect(() => {
        getPackagingTypes();
        getPackaging();
        getVendors();
        getBoxes();
        getShippingMethods();
    }, []);

    const columns = [
        { accessor: 'id', Header: 'ID' },
        {
            accessor: 'materialNumber',
            Header: 'Material Number',
            editable: true,
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0' },
        },
        {
            accessor: 'type',
            Header: 'Type',
            editable: true,
            editProps: { type: 'select', options: packagingTypesOpts },
        },
        {
            accessor: 'shippingMethod',
            Header: 'Shipping Method',
            editable: true,
            editProps: { type: 'select', options: shippingMethodOpts },
        },
        { accessor: 'description', Header: 'Description', editable: true },
        { accessor: 'vendor', Header: 'Vendor', editable: true, editProps: { type: 'select', options: vendors } },
        {
            accessor: 'cube',
            Header: 'Cube',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'lengthInches',
            Header: 'Length (in)',
            editable: true,
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0.01' },
        },
        {
            accessor: 'widthInches',
            Header: 'Width (in)',
            editable: true,
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0.01' },
        },
        {
            accessor: 'heightInches',
            Header: 'Height (in)',
            editable: true,
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0.01' },
        },
        { accessor: 'boxSize', Header: 'For Box', editable: true, editProps: { type: 'select', options: boxOpts } },
        {
            accessor: 'masterSKU',
            Header: 'SKU',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'csPallet',
            Header: 'Cases per Pallet',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'unitsPerCase',
            Header: 'Units per Case',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        { accessor: 'itemNameInternal', Header: 'Internal Name', editable: true },
        { accessor: 'itemNameExternal', Header: 'External Name', editable: true },
    ];

    return (
        <div>
            <h1>Packaging</h1>
            <Notifications options={notification} />
            <div>
                <DataTable
                    columns={columns}
                    data={rows}
                    editable
                    tableName="viewPackaging"
                    saveEdit={async ({ field, row, value }) => {
                        const update = {};
                        update[filterString(field)] = value;

                        const response = await api.editPackaging(row.id, update);
                        if (response.status === false) {
                            let message = 'Error modifying packaging';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        } else {
                            setNotification({ text: 'Packaging modified!', level: 'success' });
                            getPackaging();
                        }
                    }}
                />
            </div>
        </div>
    );
}

export { ViewPackaging };
